import * as React from 'react';
import { Temporal } from '@insio/dates';
import { cn } from '../../utils';
import { Datepicker } from '../Datepicker';
import { FilterChoiceRow } from './FilterChoiceRow';

export interface DateRangeFilterPreset {
  id: string;
  label: string;
}

export interface DateRangeFilterCustomRange {
  from?: Temporal.PlainDate;
  to?: Temporal.PlainDate;
}

/** A preset id, or a custom range with either end open. */
export type DateRangeFilterValue = { preset: string } | DateRangeFilterCustomRange;

export interface DateRangeFilterEditorProps {
  presets: DateRangeFilterPreset[];
  value: DateRangeFilterValue | undefined;
  onChange: (next: DateRangeFilterValue) => void;
  /** Label of the custom range row */
  customLabel: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  className?: string;
  testId: string;
}

const isPreset = (value: DateRangeFilterValue | undefined): value is { preset: string } =>
  value !== undefined && 'preset' in value;

const asPlainDate = (picked: unknown): Temporal.PlainDate | undefined =>
  picked instanceof Temporal.PlainDate ? picked : undefined;

/** Date filter editor: preset rows plus a custom range picked with two single-date pickers. */
export const DateRangeFilterEditor = ({
  presets,
  value,
  onChange,
  customLabel,
  fromPlaceholder,
  toPlaceholder,
  className,
  testId
}: DateRangeFilterEditorProps) => {
  const groupName = React.useId();
  const custom = value !== undefined && !isPreset(value) ? value : undefined;

  return (
    <div data-testid={testId} className={cn('flex flex-col', className)}>
      {presets.map(preset => (
        <FilterChoiceRow
          key={preset.id}
          type='radio'
          name={groupName}
          label={preset.label}
          checked={isPreset(value) && value.preset === preset.id}
          onChange={() => onChange({ preset: preset.id })}
          testId={`${testId}-preset-${preset.id}`}
        />
      ))}
      <FilterChoiceRow
        type='radio'
        name={groupName}
        label={customLabel}
        checked={custom !== undefined}
        onChange={() => onChange({})}
        testId={`${testId}-custom`}
      />
      {custom && (
        <div className='grid grid-cols-2 gap-sm px-sm pt-xs'>
          <Datepicker
            mode='single'
            height='sm'
            value={custom.from}
            placeholder={fromPlaceholder}
            disableAfter={custom.to}
            onChange={picked => onChange({ from: asPlainDate(picked), to: custom.to })}
            testId={`${testId}-from`}
          />
          <Datepicker
            mode='single'
            height='sm'
            value={custom.to}
            placeholder={toPlaceholder}
            disableBefore={custom.from}
            onChange={picked => onChange({ from: custom.from, to: asPlainDate(picked) })}
            testId={`${testId}-to`}
          />
        </div>
      )}
    </div>
  );
};

DateRangeFilterEditor.displayName = 'DateRangeFilterEditor';
